/**
 * EmailThread — the reading pane: toolbar + subject bar + messages + infobar.
 * Reacts to store.currentThreadId changes to fetch and render thread data.
 */

import { h, Fragment } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { PersonAvatar } from './PersonAvatar.jsx'
import { ThreadMessage } from './ThreadMessage.jsx'
import { ThreadInfobar } from './ThreadInfobar.jsx'
import { tagTextColor, timeAgo } from '../../modules/email/email-helpers.js'

const EMAIL_STATUSES = ['open', 'inprogress', 'closed']

export function EmailThread({ store, i18n }) {
  const thread = store.thread.value
  const loading = store.threadLoading.value
  const currentId = store.currentThreadId.value
  const commentFormVisible = store.commentFormVisible.value
  const auditLogVisible = store.auditLogVisible.value
  const auditEvents = store.auditEvents.value
  const expandedMessages = store.expandedMessages.value

  const commentBodyRef = useRef(null)

  if (!currentId) {
    return (
      <div class="email-reading-pane-empty d-flex align-items-center justify-content-center h-100 text-muted">
        <div class="text-center">
          <i class="bi bi-envelope-open d-block mb-2 opacity-40" style="font-size:3rem" />
          <span class="small">{i18n?.t('emails.select_email')}</span>
        </div>
      </div>
    )
  }

  if (loading || !thread) {
    return (
      <div class="email-reading-pane-inner d-flex align-items-center justify-content-center text-muted">
        <div class="spinner-border me-2" role="status" />
        <span>{i18n?.t('helpers.loading')}</span>
      </div>
    )
  }

  const emailItems = (thread.items || []).filter(i => i.type === 'email')
  const commentItems = (thread.items || []).filter(i => i.type === 'comment')
  const participants = thread.participants || []
  const infobar = thread.infobar || {}
  const folders = thread.email_folders || store.emailFolders.value || []
  const peopleWithUser = thread.people_with_user || []

  async function submitComment(e) {
    e.preventDefault()
    const body = commentBodyRef.current?.value?.trim()
    if (!body) return
    await store.postComment(thread.id, body)
    if (commentBodyRef.current) commentBodyRef.current.value = ''
  }

  function mobileBack() {
    store.showListPanel()
    store.clearCurrentThread()
  }

  return (
    <div class="email-reading-pane-inner">

      {/* ── Toolbar ── */}
      <div id={`email_${thread.id}_thread_toolbar`} class="email-thread-toolbar d-flex align-items-center gap-1 px-2 py-1 border-bottom">

        {/* Back (mobile) */}
        <button class="btn btn-sm btn-outline-secondary d-md-none me-1" onClick={mobileBack} title={i18n?.t('helpers.back')}>
          <i class="bi bi-arrow-left" />
        </button>

        <button class="btn btn-sm btn-outline-secondary" title={i18n?.t('emails.reply')} onClick={() => store.openCompose('reply', thread.id)}>
          <i class="bi bi-reply" />
        </button>
        <button class="btn btn-sm btn-outline-secondary d-none d-md-inline-flex" title={i18n?.t('emails.reply_all')} onClick={() => store.openCompose('reply_all', thread.id)}>
          <i class="bi bi-reply-all" />
        </button>
        <button class="btn btn-sm btn-outline-secondary" title={i18n?.t('emails.forward')} onClick={() => store.openCompose('forward', thread.id)}>
          <i class="bi bi-forward" />
        </button>
        <button class="btn btn-sm btn-outline-secondary d-none d-md-inline-flex" title={i18n?.t('emails.add_internal_note')} onClick={store.showCommentForm}>
          <i class="bi bi-sticky" />
        </button>

        <div class="vr mx-1 d-none d-md-block" />

        <ThreadStateButtons thread={thread} store={store} i18n={i18n} />

        {/* Mark unread */}
        <button class="btn btn-sm btn-outline-secondary d-none d-md-inline-flex" title={i18n?.t('emails.mark_unread')}
                onClick={() => store.updateEmailState(thread.id, 'unread')}>
          <i class="bi bi-envelope" />
        </button>

        {/* Star */}
        <StarButton thread={thread} store={store} i18n={i18n} />

        {/* Move to folder dropdown */}
        {folders.length > 0 && (
          <div class="dropdown d-none d-md-block">
            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" data-bs-strategy="fixed" title={i18n?.t('emails.move_to_folder')}>
              <i class="bi bi-folder-symlink" />
            </button>
            <ul class="dropdown-menu shadow-sm">
              <li>
                <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
                   onClick={e => { e.preventDefault(); store.moveToFolder(thread.id, null) }}>
                  <i class="bi bi-inbox" />
                  {i18n?.t('emails.move_to_inbox')}
                </a>
              </li>
              <li><hr class="dropdown-divider my-1" /></li>
              {folders.map(folder => (
                <li key={folder.id}>
                  <a
                    class={`dropdown-item d-flex align-items-center gap-2 small ${thread.email_folder_id === folder.id ? 'fw-semibold' : ''}`}
                    href="#"
                    onClick={e => { e.preventDefault(); store.moveToFolder(thread.id, folder.id) }}
                  >
                    <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
                    {folder.name}
                    {thread.email_folder_id === folder.id && <i class="bi bi-check ms-auto text-primary" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div class="vr mx-1 d-none d-md-block" />

        {/* Tag picker dropdown */}
        <TagPickerDropdown thread={thread} store={store} i18n={i18n} />

        <div class="vr mx-1 d-none d-md-block" />

        {/* Right side: status + assign */}
        <div class="ms-auto d-flex align-items-center gap-1">
          <StatusDropdown thread={thread} store={store} i18n={i18n} />
          <AssignDropdown thread={thread} store={store} i18n={i18n} peopleWithUser={peopleWithUser} />
          {/* Mobile overflow menu — actions hidden on small screens */}
          <MobileMoreMenu thread={thread} store={store} i18n={i18n} folders={folders} />
        </div>

      </div>

      {/* ── Subject bar ── */}
      <div class="email-thread-subject-bar px-2 px-md-4 pt-3 pb-2 border-bottom">
        <h4 class="mb-1 fw-semibold lh-sm">
          {thread.subject || i18n?.t('emails.no_subject')}
          {emailItems.length > 1 && (
            <span class="badge bg-secondary-subtle text-secondary fw-normal ms-1" style="font-size:0.6em;vertical-align:middle">
              {emailItems.length}
            </span>
          )}
        </h4>
        {participants.length > 1 && (
          <div class="d-flex align-items-center gap-1 mt-1">
            {participants.map((p, i) => (
              <div key={p.id} style={{ marginLeft: i > 0 ? '-6px' : '0', zIndex: 10 - i, position: 'relative' }}>
                <PersonAvatar avatarUrl={p.avatar_url} name={p.name} size={22} showTooltip cssClass="border border-2 border-white" />
              </div>
            ))}
            <span class="text-muted small ms-2">
              {participants.slice(0, 3).map(p => p.name).join(', ')}
              {participants.length > 3 && <span class="text-muted"> +{participants.length - 3}</span>}
            </span>
          </div>
        )}
      </div>

      {/* ── Body row: messages + infobar ── */}
      <div class="email-thread-body">
        <div class="email-thread-main-column">
          <div id="email_thread_content" class="email-thread-scroll">
            <div class="email-thread-items" id="email_thread_items">

              {/* Render all items (emails + comments + drafts) sorted newest-first */}
              {(thread.items || []).map(item => {
                if (item.type === 'email') {
                  return (
                    <div key={`email-${item.email.id}`} class="border-bottom">
                      <ThreadMessage
                        emailItem={item.email}
                        isExpanded={!!expandedMessages[item.email.id]}
                        store={store}
                        i18n={i18n}
                      />
                    </div>
                  )
                } else if (item.type === 'comment') {
                  return <ThreadComment key={`comment-${item.comment.id}`} comment={item.comment} store={store} i18n={i18n} />
                } else if (item.type === 'draft') {
                  return <DraftCard key={`draft-${item.draft.id}`} draft={item.draft} store={store} i18n={i18n} />
                }
                return null
              })}

              {/* Audit events (loaded on toggle) */}
              {auditLogVisible && (
                <div id="email_audit_events">
                  {store.auditEventsLoading.value ? (
                    <div class="text-center py-2 text-muted">
                      <div class="spinner-border spinner-border-sm" role="status" />
                    </div>
                  ) : (
                    auditEvents.map(event => (
                      <AuditEvent key={event.id} event={event} i18n={i18n} />
                    ))
                  )}
                </div>
              )}

              {/* Audit log toggle */}
              <div class="d-flex align-items-center justify-content-center py-2">
                <div class="form-check form-switch small text-muted">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="toggle_audit_log"
                    checked={auditLogVisible}
                    onChange={e => store.toggleAuditLog(e.target.checked)}
                  />
                  <label class="form-check-label" for="toggle_audit_log">
                    {i18n?.t('emails.show_audit_log')}
                  </label>
                </div>
              </div>

            </div>
          </div>

          {/* Reply / comment forms */}
          <div class="email-reply-sticky">
            {commentFormVisible && (
              <CommentForm
                thread={thread}
                store={store}
                i18n={i18n}
                bodyRef={commentBodyRef}
                onSubmit={submitComment}
              />
            )}
          </div>
        </div>

        {/* Infobar */}
        <div class="email-infobar border-start">
          <button
            class="btn btn-sm btn-link text-muted w-100 d-md-none py-2 border-bottom"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#email_infobar_collapse"
          >
            <i class="bi bi-info-circle me-1" />
            {i18n?.t('emails.show_infobar')}
            <i class="bi bi-chevron-down ms-1" style="font-size:0.7rem" />
          </button>
          <div class="collapse d-md-block" id="email_infobar_collapse">
            <ThreadInfobar infobar={infobar} emailId={thread.id} store={store} i18n={i18n} />
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ThreadStateButtons({ thread, store, i18n }) {
  // Detect state from the first email item's user_state (best we can do without current_person signal here)
  const firstEmail = (thread.items || []).find(i => i.type === 'email')?.email
  const state = firstEmail?.user_state || {}
  const isTrashed  = !!state.trashed_at
  const isArchived = !!state.archived_at

  if (isTrashed || isArchived) {
    return (
      <>
        <button class="btn btn-sm btn-outline-success" title={i18n?.t('emails.restore_to_inbox')}
                onClick={() => store.updateEmailState(thread.id, 'restore')}>
          <i class="bi bi-inbox" />
        </button>
        {isTrashed && (
          <button
            class="btn btn-sm btn-outline-danger"
            title={i18n?.t('emails.permanent_delete')}
            onClick={() => {
              if (confirm(i18n?.t('emails.permanent_delete_confirm'))) {
                store.updateEmailState(thread.id, 'delete')
              }
            }}
          >
            <i class="bi bi-trash3" />
          </button>
        )}
      </>
    )
  }

  return (
    <>
      <button class="btn btn-sm btn-outline-secondary" title={i18n?.t('emails.archive')}
              onClick={() => store.updateEmailState(thread.id, 'archive')}>
        <i class="bi bi-archive" />
      </button>
      <button class="btn btn-sm btn-outline-secondary" title={i18n?.t('emails.trash')}
              onClick={() => store.updateEmailState(thread.id, 'trash')}>
        <i class="bi bi-trash" />
      </button>
    </>
  )
}

function StatusDropdown({ thread, store, i18n }) {
  const statusColor = { open: 'text-secondary', inprogress: 'text-warning', closed: 'text-success' }

  return (
    <div class="dropdown">
      <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-flex align-items-center gap-1" data-bs-toggle="dropdown" data-bs-strategy="fixed">
        <i class={`bi bi-circle-fill ${statusColor[thread.status] || 'text-secondary'}`} style="font-size:0.5rem" />
        <span class="d-none d-lg-inline">{i18n?.t(`emails.status.${thread.status}`)}</span>
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm">
        {EMAIL_STATUSES.map(s => (
          <li key={s}>
            <a
              class={`dropdown-item d-flex align-items-center gap-2 ${thread.status === s ? 'fw-semibold' : ''}`}
              href="#"
              onClick={e => { e.preventDefault(); store.changeEmailStatus(thread.id, s) }}
            >
              <i class={`bi bi-circle-fill ${statusColor[s]}`} style="font-size:0.45rem" />
              {i18n?.t(`emails.status.${s}`)}
              {thread.status === s && <i class="bi bi-check ms-auto text-primary" />}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function AssignDropdown({ thread, store, i18n, peopleWithUser }) {
  const responsible = thread.responsible

  return (
    <div class="dropdown">
      <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-flex align-items-center gap-1" data-bs-toggle="dropdown" data-bs-strategy="fixed">
        {responsible
          ? (
            <>
              <PersonAvatar avatarUrl={responsible.avatar_url} name={responsible.name} size={20} />
              <span class="d-none d-lg-inline">{responsible.name}</span>
            </>
          )
          : (
            <>
              <i class="bi bi-person-plus" />
              <span class="d-none d-lg-inline">{i18n?.t('emails.assign')}</span>
            </>
          )
        }
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm" style="min-width:200px;max-height:60vh;overflow-y:auto">
        <li>
          <a class="dropdown-item text-muted small" href="#"
             onClick={e => { e.preventDefault(); store.assignResponsible(thread.id, null) }}>
            {i18n?.t('emails.unassign')}
          </a>
        </li>
        <li><hr class="dropdown-divider my-1" /></li>
        {peopleWithUser.map(person => (
          <li key={person.id}>
            <a
              class={`dropdown-item d-flex align-items-center gap-2 ${responsible?.id === person.id ? 'fw-semibold' : ''}`}
              href="#"
              onClick={e => { e.preventDefault(); store.assignResponsible(thread.id, person.id) }}
            >
              <PersonAvatar avatarUrl={person.avatar_url} name={person.name} size={24} />
              <span class="small">{person.name}</span>
              {responsible?.id === person.id && <i class="bi bi-check ms-auto text-primary" />}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ThreadComment({ comment, store, i18n }) {
  async function handleDelete(e) {
    e.preventDefault()
    if (!confirm(i18n?.t('helpers.confirm_destroy'))) return
    await store.deleteComment(comment.id)
  }

  return (
    <div id={`comment_${comment.id}_thread_item`} class="email-thread-comment border-bottom">
      <div class="d-flex align-items-start gap-2 p-3">
        <PersonAvatar avatarUrl={comment.avatar_url} name={comment.person_name} size={32} cssClass="flex-shrink-0" />
        <div class="flex-grow-1">
          <div class="d-flex align-items-baseline justify-content-between mb-1">
            <div>
              <span class="fw-semibold small">{comment.person_name}</span>
              <span class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle ms-1 small">
                <i class="bi bi-sticky small" />
                {i18n?.t('emails.internal_note')}
              </span>
            </div>
            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span class="text-muted small">{timeAgo(comment.created_at)}</span>
              {comment.is_own && (
                <a href="#" class="btn btn-link p-0 text-muted small" onClick={handleDelete}>
                  <i class="bi bi-x-lg" />
                </a>
              )}
            </div>
          </div>
          <div class="email-comment-body">{comment.body}</div>
        </div>
      </div>
    </div>
  )
}

function CommentForm({ thread, store, i18n, bodyRef, onSubmit }) {
  return (
    <div id="email_comment_form" class="px-3 pb-3">
      <div class="email-comment-form border rounded bg-warning-subtle">
        <div class="d-flex align-items-start gap-2 p-2">
          <div class="flex-grow-1">
            <div class="small fw-semibold text-warning-emphasis mb-1">
              <i class="bi bi-sticky small me-1" />
              {i18n?.t('emails.add_internal_note')}
            </div>
            <form onSubmit={onSubmit}>
              <input type="hidden" name="email_comment[email_id]" value={thread.id} />
              <textarea
                ref={bodyRef}
                class="form-control"
                placeholder={i18n?.t('emails.note_placeholder')}
                rows={3}
                required
              />
              <div class="d-flex justify-content-end gap-1 mt-1">
                <button type="button" class="btn btn-sm btn-outline-secondary" onClick={store.hideCommentForm}>
                  {i18n?.t('helpers.actions.cancel')}
                </button>
                <button type="submit" class="btn btn-sm btn-warning">
                  {i18n?.t('emails.save_note')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

function DraftCard({ draft, store, i18n }) {
  const label = draft.draft_type === 'forward'
    ? (i18n?.t('emails.forward') || 'Továbbítás')
    : (i18n?.t('emails.reply') || 'Válasz')

  const preview = draft.body_html
    ? draft.body_html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)
    : ''

  const toList = (draft.to_addresses || []).join(', ')

  function handleClick() {
    store.openComposeFromDraft(draft)
  }

  return (
    <div
      class="email-draft-card border-bottom px-3 py-2 d-flex align-items-start gap-2"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
      style="cursor:pointer"
    >
      <div class="flex-shrink-0 mt-1 text-warning">
        <i class="bi bi-pencil-square" style="font-size:1.1rem" />
      </div>
      <div class="flex-grow-1 min-width-0">
        <div class="d-flex align-items-center gap-2 mb-1">
          <span class="badge bg-warning text-dark small">
            {i18n?.t('emails.draft')}
          </span>
          <span class="text-muted small">{label}</span>
          {toList && (
            <span class="text-muted small ms-1">
              → {toList}
            </span>
          )}
        </div>
        {preview && (
          <div class="text-muted small text-truncate">{preview}</div>
        )}
      </div>
      <div class="flex-shrink-0 text-muted small align-self-center">
        <i class="bi bi-chevron-right" />
      </div>
    </div>
  )
}

function AuditEvent({ event, i18n }) {
  return (
    <div class="email-thread-audit-event py-2 px-3 border-bottom">
      <div class="d-flex align-items-center gap-2 text-muted small">
        {event.person_avatar_url
          ? <PersonAvatar avatarUrl={event.person_avatar_url} name={event.person_name} size={20} cssClass="flex-shrink-0 opacity-75" />
          : <div style="width:20px;height:20px" class="flex-shrink-0"><i class="bi bi-gear small" /></div>
        }
        <span>{event.summary}</span>
        <span class="ms-auto flex-shrink-0">{timeAgo(event.created_at)}</span>
      </div>
    </div>
  )
}

function StarButton({ thread, store, i18n }) {
  const firstEmail = (thread.items || []).find(i => i.type === 'email')?.email
  // user_state comes from the email list row (via store.emails signal)
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const isStarred = !!(emailRow?.user_state?.starred || emailRow?.user_state?.starred_at)

  return (
    <button
      class={`btn btn-sm d-none d-md-inline-flex ${isStarred ? 'btn-warning' : 'btn-outline-secondary'}`}
      title={i18n?.t('emails.star')}
      onClick={() => store.updateEmailState(thread.id, 'star')}
    >
      <i class={`bi bi-star${isStarred ? '-fill' : ''}`} />
    </button>
  )
}

function TagPickerDropdown({ thread, store, i18n }) {
  const allTags = store.emailTags.value
  const appliedTagIds = thread.email_tag_ids || []
  const [search, setSearch] = useState('')
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6c757d')
  const [creating, setCreating] = useState(false)

  if (!allTags.length) return null

  const filtered = search
    ? allTags.filter(t => t.tag.toLowerCase().includes(search.toLowerCase()))
    : allTags

  async function handleCreate(e) {
    e.preventDefault()
    if (!newTagName.trim()) return
    setCreating(true)
    await store.createTag(newTagName.trim(), newTagColor)
    setNewTagName('')
    setNewTagColor('#6c757d')
    setCreating(false)
  }

  return (
    <div class="dropdown d-none d-md-block">
      <button
        class="btn btn-sm btn-outline-secondary dropdown-toggle"
        title={i18n?.t('emails.tags_label')}
        data-bs-toggle="dropdown"
        data-bs-strategy="fixed"
        data-bs-auto-close="outside"
      >
        <i class="bi bi-tag" />
      </button>
      <div class="dropdown-menu shadow-sm p-2" style="min-width:220px">
        <input
          type="text"
          class="form-control form-control-sm mb-2"
          placeholder={i18n?.t('emails.tag_search_placeholder')}
          value={search}
          onInput={e => setSearch(e.target.value)}
        />
        <div style="max-height:200px; overflow-y:auto">
          {filtered.map(tag => (
            <label
              key={tag.id}
              class="dropdown-item d-flex align-items-center gap-2 small rounded py-1 px-2"
              style="cursor:pointer"
            >
              <input
                type="checkbox"
                class="form-check-input mt-0"
                checked={appliedTagIds.includes(tag.id)}
                onChange={() => store.toggleTag(thread.id, tag.id)}
              />
              <span
                class="badge rounded-pill"
                style={{ backgroundColor: tag.color || '#6c757d', color: tagTextColor(tag.color || '#6c757d'), fontSize: '0.7rem' }}
              >
                {tag.tag}
              </span>
            </label>
          ))}
          {filtered.length === 0 && (
            <div class="text-muted small px-2 py-1">{i18n?.t('emails.no_tags_found')}</div>
          )}
        </div>
        <hr class="my-1" />
        <form class="d-flex gap-1" onSubmit={handleCreate}>
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder={i18n?.t('emails.new_tag')}
            value={newTagName}
            onInput={e => setNewTagName(e.target.value)}
          />
          <input
            type="color"
            class="form-control form-control-sm form-control-color p-0 border-0"
            value={newTagColor}
            style="width:28px;height:28px"
            onInput={e => setNewTagColor(e.target.value)}
          />
          <button type="submit" class="btn btn-sm btn-outline-primary flex-shrink-0" disabled={creating}>
            <i class="bi bi-plus" />
          </button>
        </form>
      </div>
    </div>
  )
}

function MobileMoreMenu({ thread, store, i18n, folders }) {
  const allTags = store.emailTags.value
  const appliedTagIds = thread.email_tag_ids || []
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const isStarred = !!(emailRow?.user_state?.starred || emailRow?.user_state?.starred_at)

  return (
    <div class="dropdown d-md-none">
      <button
        class="btn btn-sm btn-outline-secondary dropdown-toggle"
        data-bs-toggle="dropdown"
        data-bs-strategy="fixed"
        data-bs-auto-close="outside"
        title={i18n?.t('helpers.more_actions') || 'Több akció'}
      >
        <i class="bi bi-three-dots" />
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm">

        {/* Reply All */}
        <li>
          <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
             onClick={e => { e.preventDefault(); store.openCompose('reply_all', thread.id) }}>
            <i class="bi bi-reply-all" />
            {i18n?.t('emails.reply_all')}
          </a>
        </li>

        {/* Add note */}
        <li>
          <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
             onClick={e => { e.preventDefault(); store.showCommentForm() }}>
            <i class="bi bi-sticky" />
            {i18n?.t('emails.add_internal_note')}
          </a>
        </li>

        <li><hr class="dropdown-divider my-1" /></li>

        {/* Star toggle */}
        <li>
          <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
             onClick={e => { e.preventDefault(); store.updateEmailState(thread.id, 'star') }}>
            <i class={`bi bi-star${isStarred ? '-fill text-warning' : ''}`} />
            {i18n?.t(isStarred ? 'emails.unstar' : 'emails.star')}
          </a>
        </li>

        {/* Mark unread */}
        <li>
          <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
             onClick={e => { e.preventDefault(); store.updateEmailState(thread.id, 'unread') }}>
            <i class="bi bi-envelope" />
            {i18n?.t('emails.mark_unread')}
          </a>
        </li>

        {/* Move to folder */}
        {folders.length > 0 && (
          <>
            <li><hr class="dropdown-divider my-1" /></li>
            <li>
              <span class="dropdown-item-text text-muted small">{i18n?.t('emails.move_to_folder')}</span>
            </li>
            <li>
              <a class="dropdown-item d-flex align-items-center gap-2 small" href="#"
                 onClick={e => { e.preventDefault(); store.moveToFolder(thread.id, null) }}>
                <i class="bi bi-inbox" />
                {i18n?.t('emails.move_to_inbox')}
              </a>
            </li>
            {folders.map(folder => (
              <li key={folder.id}>
                <a
                  class={`dropdown-item d-flex align-items-center gap-2 small ${thread.email_folder_id === folder.id ? 'fw-semibold' : ''}`}
                  href="#"
                  onClick={e => { e.preventDefault(); store.moveToFolder(thread.id, folder.id) }}
                >
                  <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
                  {folder.name}
                  {thread.email_folder_id === folder.id && <i class="bi bi-check ms-auto text-primary" />}
                </a>
              </li>
            ))}
          </>
        )}

        {/* Tags */}
        {allTags.length > 0 && (
          <>
            <li><hr class="dropdown-divider my-1" /></li>
            <li>
              <span class="dropdown-item-text text-muted small">{i18n?.t('emails.tags_label')}</span>
            </li>
            {allTags.map(tag => (
              <li key={tag.id}>
                <label class="dropdown-item d-flex align-items-center gap-2 small" style="cursor:pointer">
                  <input
                    type="checkbox"
                    class="form-check-input mt-0"
                    checked={appliedTagIds.includes(tag.id)}
                    onChange={() => store.toggleTag(thread.id, tag.id)}
                  />
                  <span
                    class="badge rounded-pill"
                    style={{ backgroundColor: tag.color || '#6c757d', color: tagTextColor(tag.color || '#6c757d'), fontSize: '0.7rem' }}
                  >
                    {tag.tag}
                  </span>
                </label>
              </li>
            ))}
          </>
        )}

      </ul>
    </div>
  )
}